import React from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  Pill,
  Heart,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { imgUrl, eur } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();

  const img = (product.images || [])[0];

  const onSale =
    product.compare_price &&
    product.compare_price > product.price;

  const discountPercent = onSale
    ? Math.round(
        ((product.compare_price - product.price) /
          product.compare_price) *
          100
      )
    : 0;

  const wished = has(product.id);

  const inStock = product.stock > 0;

  /* =========================================================
     PRODUCT DESCRIPTION
  ========================================================= */

  const shortDescription =
    product.description
      ?.replace(/<[^>]*>/g, "")
      ?.replace(/\s+/g, " ")
      ?.trim() || "";

  /* =========================================================
     ADD TO CART
  ========================================================= */

  const add = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!inStock) return;

    addItem(product, 1);

    toast.success("Προστέθηκε στο καλάθι");
  };

  /* =========================================================
     WISHLIST
  ========================================================= */

  const fav = (e) => {
    e.preventDefault();
    e.stopPropagation();

    toggle(product);

    toast.success(
      wished
        ? "Αφαιρέθηκε από τα αγαπημένα"
        : "Προστέθηκε στα αγαπημένα"
    );
  };

  /* =========================================================
     SEO PRODUCT URL
  ========================================================= */

  const productSlug =
    product.slug || product.id;

  return (
    <Link
      to={`/product/${productSlug}`}
      className="group block h-full"
      data-testid={`product-card-${product.id}`}
    >
      <article className="h-full flex flex-col">

        {/* =====================================================
            PRODUCT IMAGE
        ===================================================== */}

        <div className="relative overflow-hidden rounded-sm border border-[#E5E9E4] bg-white aspect-square">

          {img ? (
            <img
              src={imgUrl(img)}
              alt={`${product.name}${
                product.brand
                  ? ` ${product.brand}`
                  : ""
              } | Φαρμακείο Κατιρτζόγλου`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-[#E8F0E1] text-[#1E3A2F]">
              <Pill className="h-12 w-12 opacity-40" />
            </div>
          )}

          {/* ===================================================
              SALE BADGES
          =================================================== */}

          {onSale && (
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">

              <span className="bg-[#C25953] text-white text-[11px] font-bold px-2.5 py-1 rounded-sm uppercase tracking-wide">
                Προσφορά
              </span>

              <span className="bg-white text-[#C25953] text-xs font-bold px-2.5 py-1 rounded-sm shadow-sm">
                -{discountPercent}%
              </span>

            </div>
          )}

          {/* ===================================================
              WISHLIST
          =================================================== */}

          <button
            onClick={fav}
            data-testid={`wishlist-toggle-${product.id}`}
            className="absolute top-3 right-3 p-2.5 rounded-full bg-white/95 shadow-sm hover:bg-white hover:scale-105 transition-all duration-200"
            aria-label={
              wished
                ? "Αφαίρεση από τα αγαπημένα"
                : "Προσθήκη στα αγαπημένα"
            }
            type="button"
          >
            <Heart
              className={`h-5 w-5 ${
                wished
                  ? "fill-[#C25953] text-[#C25953]"
                  : "text-[#1E3A2F]"
              }`}
            />
          </button>

          {/* ===================================================
              STOCK STATUS
          =================================================== */}

          {!inStock && (
            <span className="absolute bottom-3 left-3 bg-[#536D61] text-white text-[11px] font-medium px-2.5 py-1.5 rounded-sm">
              Εξαντλημένο
            </span>
          )}

          {inStock && (
            <span className="absolute bottom-3 left-3 flex items-center gap-1 bg-white/95 text-[#1E3A2F] text-[10px] sm:text-[11px] font-medium px-2 py-1 rounded-sm shadow-sm">
              <Check className="h-3 w-3" />
              Διαθέσιμο
            </span>
          )}

          {/* ===================================================
              QUICK ADD TO CART
          =================================================== */}

          <button
            onClick={add}
            disabled={!inStock}
            data-testid={`add-to-cart-${product.id}`}
            type="button"
            className="absolute bottom-3 right-3 flex items-center justify-center gap-2 bg-[#1E3A2F] text-white h-10 px-3 sm:px-4 rounded-sm opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#111C18] disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Προσθήκη στο καλάθι"
          >
            <ShoppingBag className="h-4 w-4" />

            <span className="hidden sm:inline text-xs font-semibold">
              Προσθήκη
            </span>
          </button>
        </div>

        {/* =====================================================
            PRODUCT INFORMATION
        ===================================================== */}

        <div className="mt-3 flex flex-col flex-1">

          {/* ===================================================
              BRAND
          =================================================== */}

          {product.brand && (
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.12em] text-[#536D61] font-medium">
              {product.brand}
            </p>
          )}

          {/* ===================================================
              PRODUCT NAME
          =================================================== */}

          <h3 className="text-sm font-medium text-[#111C18] line-clamp-2 leading-snug mt-1 min-h-[40px] group-hover:text-[#1E3A2F] transition-colors">
            {product.name}
          </h3>

          {/* ===================================================
              SHORT DESCRIPTION
          =================================================== */}

          {shortDescription && (
            <p className="mt-1.5 text-xs text-[#687B73] leading-relaxed line-clamp-2 min-h-[32px]">
              {shortDescription}
            </p>
          )}

          {/* ===================================================
              PRICE
          =================================================== */}

          <div className="mt-2.5 flex items-center gap-2 flex-wrap">

            <span
              className={`text-base sm:text-lg font-semibold ${
                onSale
                  ? "text-[#C25953]"
                  : "text-[#1E3A2F]"
              }`}
              data-testid={`product-price-${product.id}`}
            >
              {eur(product.price)}
            </span>

            {onSale && (
              <span className="text-xs sm:text-sm text-[#789087] line-through">
                {eur(product.compare_price)}
              </span>
            )}

            {onSale && (
              <span className="text-[10px] sm:text-xs font-semibold text-[#C25953] bg-[#FFF1EF] px-1.5 py-0.5 rounded-sm">
                -{discountPercent}%
              </span>
            )}
          </div>

          {/* ===================================================
              AVAILABILITY
          =================================================== */}

          <div className="mt-1.5 min-h-[20px]">

            {inStock ? (
              <span className="text-xs text-[#536D61]">
                Σε απόθεμα
              </span>
            ) : (
              <span className="text-xs text-[#89978F]">
                Προσωρινά μη διαθέσιμο
              </span>
            )}

          </div>

          {/* ===================================================
              ADD TO CART
          =================================================== */}

          <div className="mt-3">

            <button
              onClick={add}
              disabled={!inStock}
              type="button"
              data-testid={`add-to-cart-bottom-${product.id}`}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-sm border border-[#1E3A2F] text-[#1E3A2F] text-xs sm:text-sm font-semibold hover:bg-[#1E3A2F] hover:text-white transition-all duration-200 disabled:border-[#D8DEDA] disabled:text-[#89978F] disabled:bg-[#F5F7F4] disabled:cursor-not-allowed"
              aria-label={
                inStock
                  ? "Προσθήκη στο καλάθι"
                  : "Εξαντλημένο"
              }
            >
              <ShoppingBag className="h-4 w-4" />

              {inStock
                ? "Προσθήκη στο καλάθι"
                : "Εξαντλημένο"}
            </button>

          </div>
        </div>
      </article>
    </Link>
  );
}